import { mergeClasses } from "@fluentui/react-components";
import {
  ArrowDown20Regular,
  ArrowUp20Regular,
  Edit20Regular,
  Eye20Regular,
} from "@fluentui/react-icons";
import {
  deleteAccessLLMModel,
  getAllAccessLLMModels,
  updateAccessLLMModel,
} from "api";
import { ConstantValues } from "common/constants";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { AdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { useAdminListFilters } from "hooks/useAdminListFilters";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ChevronIcon } from "icons/ChevronIcon";
import { ExportIcon } from "icons/ExportIcon";
import { SearchIcon } from "icons/SearchIcon";
import { IAccessLLMModel } from "models/IAccessLLMModel";
import { ITableColumn } from "models/ITableColumn";
import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
  useSharedAdminContentHeaderStyles,
  useSharedButtonStyles,
} from "theme/sharedStyles";
import { downloadCSV, generateCSV } from "utils/generalUtils";
import { EditAccessLLMModel } from "./editDialog/EditAccessLLMModel";
import { ViewAccessLLMModel } from "./viewDialog/ViewAccessLLMModel";

const getProvider = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower.includes("claude")) return "Anthropic";
  if (lower.includes("openai") || lower.includes("gpt")) return "OpenAI";
  if (lower.includes("llama")) return "Meta";
  return "Other";
};

export const ManageAccessLLMModels = (): JSX.Element => {
  const queryClient = useQueryClient();
  const { data: accessLLMModels = [] } = useQuery(
    "allAccessLLMModels",
    getAllAccessLLMModels,
    { refetchOnWindowFocus: false, staleTime: 0 },
  );
  const [viewingModel, setViewingModel] = useState<IAccessLLMModel | null>(
    null,
  );
  const [isViewDialogOpen, setIsViewDialogOpen] = useState<boolean>(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState<boolean>(false);
  const [editingModel, setEditingModel] = useState<IAccessLLMModel | null>(
    null,
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deletingModel, setDeletingModel] = useState<IAccessLLMModel | null>(
    null,
  );
  const { filters, setFilter } = useAdminListFilters({
    cacheKey: "AccessLLMModels",
    filterKeys: ["provider", "accessType"] as const,
  });
  const colStyles = useAdminColumnStyles();

  const columns: ITableColumn<IAccessLLMModel>[] = useMemo(
    () => [
      {
        columnId: "order",
        displayValue: "Order",
        className: colStyles.narrow,
        renderHeaderCell: () => "Order",
        renderCell: (item: IAccessLLMModel) => item.order,
        compare: (a: IAccessLLMModel, b: IAccessLLMModel) =>
          (a.order ?? 0) - (b.order ?? 0),
      },
      {
        columnId: "name",
        displayValue: "Name",
        className: colStyles.normal,
        renderHeaderCell: () => "Name",
        renderCell: (item: IAccessLLMModel) => item.name,
        compare: (a: IAccessLLMModel, b: IAccessLLMModel) =>
          a.name.localeCompare(b.name),
      },
      {
        columnId: "displayName",
        displayValue: "Display Name",
        className: colStyles.normal,
        renderHeaderCell: () => "Display Name",
        renderCell: (item: IAccessLLMModel) => item.displayName,
        compare: (a: IAccessLLMModel, b: IAccessLLMModel) =>
          a.displayName.localeCompare(b.displayName),
      },
      {
        columnId: "accessType",
        displayValue: "Access Type",
        className: colStyles.narrow,
        renderHeaderCell: () => "Access Type",
        renderCell: (item: IAccessLLMModel) => item.accessType,
        compare: (a: IAccessLLMModel, b: IAccessLLMModel) =>
          a.accessType.localeCompare(b.accessType),
      },
    ],
    [colStyles],
  );

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<IAccessLLMModel>({
    cacheKey: "AccessLLMModels",
    defaultSortColumn: "order",
    defaultDescending: false,
  });

  const searchFields = useMemo(
    () => ["name", "displayName", "accessType"] as (keyof IAccessLLMModel)[],
    [],
  );
  const {
    searchQuery: internalSearchQuery,
    handleSearchChange,
    handleSearchValueChange,
    filteredItems,
  } = useAdminListSearch<IAccessLLMModel>({
    items: accessLLMModels,
    searchFields,
  });

  const providerOptions = useMemo(() => {
    const providers = new Set(accessLLMModels.map((m) => getProvider(m.name)));
    return Array.from(providers)
      .sort((a, b) => a.localeCompare(b))
      .map((p) => ({ key: p, text: p }));
  }, [accessLLMModels]);

  const accessTypeOptions = useMemo(
    () => [
      { key: "public", text: "Public" },
      { key: "limited", text: "Limited" },
      { key: "disabled", text: "Disabled" },
    ],
    [],
  );

  const fullyFilteredItems = useMemo(() => {
    let result = filteredItems;
    if (filters.provider)
      result = result.filter((m) => getProvider(m.name) === filters.provider);
    if (filters.accessType)
      result = result.filter((m) => m.accessType === filters.accessType);
    return result;
  }, [filteredItems, filters.provider, filters.accessType]);

  const sortedItems = useMemo(
    () => sortItems(fullyFilteredItems, columns),
    [fullyFilteredItems, sortItems, columns],
  );

  const emptyStateMessage = useMemo(() => {
    const hasActiveFilters =
      internalSearchQuery || filters.provider || filters.accessType;
    return hasActiveFilters
      ? "No models match the current filters."
      : "No models found.";
  }, [internalSearchQuery, filters.provider, filters.accessType]);

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<IAccessLLMModel>({
    items: sortedItems,
    itemsPerPage: 10,
  });

  const handleViewModel = (model: IAccessLLMModel) => {
    setViewingModel(model);
    setIsViewDialogOpen(true);
  };

  const handleEditModel = (model: IAccessLLMModel) => {
    setEditingModel(model);
    setIsEditPopupOpen(true);
  };

  const handleMoveUp = async (model: IAccessLLMModel) => {
    try {
      const index = sortedItems.findIndex((m) => m.id === model.id);
      if (index <= 0) return;

      const modelToMove = sortedItems[index];
      const targetModel = sortedItems[index - 1];

      const updatedModelA = { ...modelToMove, order: index - 1 };
      const updatedModelB = { ...targetModel, order: index };

      await updateAccessLLMModel({
        modelId: updatedModelA.id,
        ...updatedModelA,
      });
      await updateAccessLLMModel({
        modelId: updatedModelB.id,
        ...updatedModelB,
      });

      queryClient.invalidateQueries("allAccessLLMModels");
    } catch (error) {
      console.error("Error reordering models:", error);
    }
  };

  const handleMoveDown = async (model: IAccessLLMModel) => {
    try {
      const index = sortedItems.findIndex((m) => m.id === model.id);
      if (index >= sortedItems.length - 1) return;

      const modelToMove = sortedItems[index];
      const targetModel = sortedItems[index + 1];

      const updatedModelA = { ...modelToMove, order: index + 1 };
      const updatedModelB = { ...targetModel, order: index };

      await updateAccessLLMModel({
        modelId: updatedModelA.id,
        ...updatedModelA,
      });
      await updateAccessLLMModel({
        modelId: updatedModelB.id,
        ...updatedModelB,
      });

      queryClient.invalidateQueries("allAccessLLMModels");
    } catch (error) {
      console.error("Error reordering models:", error);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingModel) return;
    try {
      await deleteAccessLLMModel({ modelId: deletingModel.id });
      queryClient.invalidateQueries("allAccessLLMModels");
    } catch (error) {
      console.error("Error deleting model:", error);
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingModel(null);
    }
  };

  const handleCloseEditDialog = () => {
    setIsEditPopupOpen(false);
    setEditingModel(null);
  };

  const handleEditSuccess = async () => {
    handleCloseEditDialog();
  };

  const rowActions: AdminRowAction<IAccessLLMModel>[] = useMemo(
    () => [
      {
        icon: <Eye20Regular />,
        tooltip: "View",
        onClick: handleViewModel,
      },
      {
        icon: <ArrowUp20Regular />,
        tooltip: "Move Up",
        onClick: handleMoveUp,
        disabled: (item: IAccessLLMModel) => {
          const index = sortedItems.findIndex((m) => m.id === item.id);
          return index <= 0;
        },
      },
      {
        icon: <ArrowDown20Regular />,
        tooltip: "Move Down",
        onClick: handleMoveDown,
        disabled: (item: IAccessLLMModel) => {
          const index = sortedItems.findIndex((m) => m.id === item.id);
          return index >= sortedItems.length - 1;
        },
      },
      {
        icon: <Edit20Regular />,
        tooltip: "Edit",
        onClick: handleEditModel,
      },
    ],
    [sortedItems],
  );

  const sidebarItem = useMemo(() => findSidebarItem("aiAccess"), []);

  const headerClasses = useSharedAdminContentHeaderStyles();
  const buttonStyles = useSharedButtonStyles();

  const handleExport = useCallback((): void => {
    const csv = generateCSV(sortedItems, [
      { header: "Order", accessor: (m) => String(m.order ?? "") },
      { header: "Name", accessor: (m) => m.name || "" },
      { header: "Display Name", accessor: (m) => m.displayName || "" },
      { header: "Access Type", accessor: (m) => m.accessType || "" },
    ]);
    downloadCSV(csv, "ai-models.csv");
  }, [sortedItems]);

  return (
    <>
      <div className={headerClasses.root}>
        <div
          className={mergeClasses(
            headerClasses.titleRow,
            headerClasses.titleRowWithFilters,
          )}
        >
          <h1 className={headerClasses.title}>
            {sidebarItem?.label || "AI Models Access"}
          </h1>
          <div className={headerClasses.actions}>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.secondaryButton,
              )}
              onClick={handleExport}
              type="button"
            >
              <ExportIcon /> Export
            </button>
          </div>
        </div>
        <div className={headerClasses.filterRow}>
          <div className={headerClasses.searchInputWrapper}>
            <SearchIcon className={headerClasses.searchIcon} />
            <input
              type="text"
              className={headerClasses.searchInput}
              placeholder="Search models..."
              value={internalSearchQuery}
              onChange={(e) => handleSearchValueChange(e.target.value)}
              aria-label="Search models..."
            />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.provider}
              onChange={(e) => setFilter("provider", e.target.value)}
              aria-label="All Providers"
            >
              <option value="">All Providers</option>
              {providerOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.text}
                </option>
              ))}
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.accessType}
              onChange={(e) => setFilter("accessType", e.target.value)}
              aria-label="All Access Types"
            >
              <option value="">All Access Types</option>
              {accessTypeOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.text}
                </option>
              ))}
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
        </div>
      </div>
      <AdminListPage<IAccessLLMModel>
        title="Access LLM Models"
        items={accessLLMModels}
        columns={columns}
        isLoading={false}
        headerActions={[]}
        searchPlaceholder="Search models..."
        searchQuery={internalSearchQuery}
        onSearchChange={handleSearchChange}
        hideHeader={true}
        rowActions={rowActions}
        actionsWidth="140px"
        getRowId={(model) => model.id ?? ""}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={emptyStateMessage}
      />

      {isViewDialogOpen && viewingModel && (
        <ViewAccessLLMModel
          isOpen={isViewDialogOpen}
          onDismiss={() => {
            setIsViewDialogOpen(false);
            setViewingModel(null);
          }}
          model={viewingModel}
        />
      )}

      {isEditPopupOpen && editingModel && (
        <EditAccessLLMModel
          accessLLMModel={editingModel}
          isOpen={isEditPopupOpen}
          onDismiss={(isSaved): void => {
            if (isSaved) {
              handleEditSuccess();
            } else {
              handleCloseEditDialog();
            }
          }}
        />
      )}

      <ActionDialog
        isVisible={isDeleteDialogOpen}
        title="Delete Model"
        message={`Are you sure you want to delete "${deletingModel?.name}"? This action cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => {
          setIsDeleteDialogOpen(false);
          setDeletingModel(null);
        }}
      />
    </>
  );
};
